import { useId, useMemo } from "react";
import { Plus, Trash2 } from "lucide-react";
import { ChipInput } from "../ChipInput/ChipInput";
import { Input } from "../Input/Input";
import { Select } from "../Select/Select";
import styles from "./MetaSchemaEditor.module.css";

export type MetaFieldType = "string" | "string[]" | "int" | "bool";

export type MetaSchemaField = {
  key: string;
  type: MetaFieldType;
  /**
   * Optional whitelist of accepted values. Applies to `string` and `string[]`
   * only; empty / absent means any value of the declared type is accepted.
   */
  allowed?: string[];
};

interface Props {
  value: MetaSchemaField[];
  onChange: (next: MetaSchemaField[]) => void;
  /** Optional regex for keys. Default `^[a-z_][a-z0-9_]*$`. */
  keyPattern?: RegExp;
  label?: string;
  helperText?: string;
  disabled?: boolean;
}

const DEFAULT_KEY_PATTERN = /^[a-z_][a-z0-9_]*$/;

const TYPE_OPTIONS = [
  { value: "string", label: "string" },
  { value: "string[]", label: "string[]" },
  { value: "int", label: "int" },
  { value: "bool", label: "bool" },
];

/** The allow-list only makes sense for the string-ish types. */
export function supportsAllowList(type: MetaFieldType): boolean {
  return type === "string" || type === "string[]";
}

/**
 * MetaSchemaEditor — editor for `profile.meta_schema` entries
 * (`{ key, type, allowed? }`).
 *
 * Deliberately NOT an extension of SchemaEditor: that one is shared with
 * `guild.metadata_schema`, where an allow-list is dead weight. This editor
 * reuses the same Input / Select / ChipInput primitives but lays each entry
 * out as a two-row card — key + type + delete on top, allow-list below — so a
 * 10–15 value whitelist gets the full width instead of a cramped third grid
 * column.
 *
 * Mockup: config-profile-meta-schema.html §1 (populated), §2 (empty + errors).
 */
export function MetaSchemaEditor({
  value,
  onChange,
  keyPattern = DEFAULT_KEY_PATTERN,
  label,
  helperText,
  disabled,
}: Props) {
  const reactId = useId();
  const labelId = label ? `${reactId}-label` : undefined;

  // Track which keys collide (case-sensitive: the pattern restricts keys to
  // lowercase letters/digits/underscore anyway).
  const dupSet = useMemo(() => {
    const seen = new Map<string, number>();
    const dups = new Set<string>();
    value.forEach((f) => {
      if (!f.key) return;
      const n = (seen.get(f.key) ?? 0) + 1;
      seen.set(f.key, n);
      if (n > 1) dups.add(f.key);
    });
    return dups;
  }, [value]);

  function updateRow(idx: number, next: Partial<MetaSchemaField>) {
    onChange(value.map((row, i) => (i === idx ? { ...row, ...next } : row)));
  }

  /**
   * Switching to a type that has no allow-list drops the stored values. The
   * server ignores `allowed` on int/bool anyway, so keeping them would only
   * write dead config the next editor would puzzle over.
   */
  function changeType(idx: number, type: MetaFieldType) {
    updateRow(idx, supportsAllowList(type) ? { type } : { type, allowed: [] });
  }

  function removeRow(idx: number) {
    onChange(value.filter((_, i) => i !== idx));
  }

  function addRow() {
    onChange([...value, { key: "", type: "string", allowed: [] }]);
  }

  return (
    <div className={styles.wrap}>
      {label && (
        <span id={labelId} className={styles.label}>
          {label}
        </span>
      )}
      <div className={styles.editor} role="group" aria-labelledby={labelId}>
        {value.map((row, idx) => {
          const duplicate = !!row.key && dupSet.has(row.key);
          const patternInvalid = !!row.key && !keyPattern.test(row.key);
          const errMsg = duplicate
            ? "Duplicate key"
            : patternInvalid
              ? "Use lowercase letters, digits, underscore"
              : undefined;
          const allowed = row.allowed ?? [];
          const hasAllowList = supportsAllowList(row.type);
          return (
            <div
              className={`${styles.row} ${errMsg ? styles.rowInvalid : ""}`}
              key={idx}
            >
              <div className={styles.head}>
                <Input
                  aria-label={`Field ${idx + 1} key`}
                  value={row.key}
                  onChange={(e) => updateRow(idx, { key: e.target.value })}
                  placeholder="field_name"
                  disabled={disabled}
                  error={errMsg}
                />
                <Select
                  className={styles.typeSelect}
                  value={row.type}
                  onChange={(v) => changeType(idx, v as MetaFieldType)}
                  options={TYPE_OPTIONS}
                  disabled={disabled}
                  monospaceValue
                />
                <button
                  type="button"
                  className={styles.delete}
                  aria-label={`Delete field ${idx + 1}`}
                  disabled={disabled}
                  onClick={() => removeRow(idx)}
                >
                  <Trash2 size={14} aria-hidden />
                </button>
              </div>

              {hasAllowList ? (
                <div className={styles.allowed}>
                  <div className={styles.caption}>
                    Allowed values{" "}
                    <span className={styles.count}>
                      {allowed.length > 0 ? allowed.length : "empty — any value"}
                    </span>
                  </div>
                  <ChipInput
                    value={allowed}
                    onChange={(next) => updateRow(idx, { allowed: next })}
                    placeholder="Add value…"
                    disabled={disabled}
                    inputAriaLabel={`Field ${idx + 1} allowed values`}
                    helperText={
                      row.type === "string[]"
                        ? "Every element of the array is checked against this list."
                        : undefined
                    }
                  />
                </div>
              ) : (
                <div className={styles.na}>
                  Allow-list applies to <code className={styles.code}>string</code>{" "}
                  and <code className={styles.code}>string[]</code> only.
                </div>
              )}
            </div>
          );
        })}
        <button
          type="button"
          className={styles.addBtn}
          onClick={addRow}
          disabled={disabled}
        >
          <Plus size={12} aria-hidden />
          Add field
        </button>
      </div>
      {helperText && <div className={styles.helper}>{helperText}</div>}
    </div>
  );
}
